import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_4de1dc0a from 'nuxt_plugin_sentryserver_4de1dc0a' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_2faf2afc from 'nuxt_plugin_sentryclient_2faf2afc' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_polyfill_14930cc5 from 'nuxt_plugin_polyfill_14930cc5' // Source: ../plugins/polyfill (mode: 'client')
import nuxt_plugin_cosmosWalletclient_1b23f452 from 'nuxt_plugin_cosmosWalletclient_1b23f452' // Source: ../plugins/cosmosWallet.client.js (mode: 'client')
import nuxt_plugin_vuecookie_41e86efe from 'nuxt_plugin_vuecookie_41e86efe' // Source: ../plugins/vue-cookie (mode: 'client')
import nuxt_plugin_vuei18n_9dc1c90a from 'nuxt_plugin_vuei18n_9dc1c90a' // Source: ../plugins/vue-i18n (mode: 'all')
import nuxt_plugin_gtagclient_a0de0632 from 'nuxt_plugin_gtagclient_a0de0632' // Source: ../plugins/gtag.client.js (mode: 'client')
import nuxt_plugin_vuematerial_7da81004 from 'nuxt_plugin_vuematerial_7da81004' // Source: ../plugins/vue-material (mode: 'all')
import nuxt_plugin_likecoinuivue_c930e148 from 'nuxt_plugin_likecoinuivue_c930e148' // Source: ../plugins/likecoin-ui-vue (mode: 'all')
import nuxt_plugin_vuesimplesvg_73efa366 from 'nuxt_plugin_vuesimplesvg_73efa366' // Source: ../plugins/vue-simple-svg (mode: 'all')
import nuxt_plugin_vueimagelightbox_7b24aa44 from 'nuxt_plugin_vueimagelightbox_7b24aa44' // Source: ../plugins/vue-image-lightbox (mode: 'client')
import nuxt_plugin_vueclipboard2_3c353cf9 from 'nuxt_plugin_vueclipboard2_3c353cf9' // Source: ../plugins/vue-clipboard2 (mode: 'client')
import nuxt_plugin_vueportal_158772ee from 'nuxt_plugin_vueportal_158772ee' // Source: ../plugins/vue-portal (mode: 'client')
import nuxt_plugin_vuescrollreveal_36883210 from 'nuxt_plugin_vuescrollreveal_36883210' // Source: ../plugins/vue-scroll-reveal (mode: 'client')
import nuxt_plugin_vuescroll_0c9cb62c from 'nuxt_plugin_vuescroll_0c9cb62c' // Source: ../plugins/vue-scroll (mode: 'client')
import nuxt_plugin_vuejsmodal_f50827f4 from 'nuxt_plugin_vuejsmodal_f50827f4' // Source: ../plugins/vue-js-modal (mode: 'all')
import nuxt_plugin_gsapclient_05cf81a2 from 'nuxt_plugin_gsapclient_05cf81a2' // Source: ../plugins/gsap.client.js (mode: 'client')
import nuxt_plugin_AuthCoreclient_2cbd409b from 'nuxt_plugin_AuthCoreclient_2cbd409b' // Source: ../plugins/AuthCore.client.js (mode: 'client')
import nuxt_plugin_crispclient_7cd6549f from 'nuxt_plugin_crispclient_7cd6549f' // Source: ../plugins/crisp.client.js (mode: 'client')
import nuxt_plugin_testing_dbf3344c from 'nuxt_plugin_testing_dbf3344c' // Source: ../plugins/testing (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$nuxt : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"titleTemplate":(titleChunk) => {
      const title = 'LikeCoin - Decentralize Publishing';
      return titleChunk ?
      `${titleChunk} - ${title}` : title;
    },"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"name":"mobile-web-app-capable","content":"yes"},{"http-equiv":"cache-control","content":"no-cache"},{"hid":"description","name":"description","content":"Decentralize Publishing. Own your content forever."},{"hid":"og_title","property":"og:title","content":"LikeCoin - Decentralize Publishing"},{"hid":"og_description","property":"og:description","content":"Decentralize Publishing. Own your content forever."},{"hid":"og_image","property":"og:image","content":"https:\u002F\u002Flike.co\u002Fimages\u002Fog\u002Fdefault.png"},{"hid":"og_image_alt","property":"og:image:alt","content":"LikeCoin"},{"hid":"og_image_width","property":"og:image:width","content":"1200"},{"hid":"og_image_height","property":"og:image:height","content":"630"},{"hid":"theme-color","name":"theme-color","content":"#D2F0F0"}],"script":[{"hid":"schema","innerHTML":"[{\"@context\":\"https:\u002F\u002Fschema.org\",\"@type\":\"WebSite\",\"name\":\"LikeCoin\",\"url\":\"https:\u002F\u002Flike.co\u002F\"},{\"@context\":\"http:\u002F\u002Fwww.schema.org\",\"@type\":\"Project\",\"name\":\"LikeCoin\",\"url\":\"https:\u002F\u002Flike.co\",\"logo\":\"https:\u002F\u002Flike.co\u002Flogo.png\",\"sameAs\":[\"https:\u002F\u002Fblog.like.co\",\"https:\u002F\u002Fgithub.com\u002Flikecoin\",\"https:\u002F\u002Ftwitter.com\u002Flikecoin\",\"https:\u002F\u002Fwww.youtube.com\u002Fc\u002FLikeCoin\",\"https:\u002F\u002Fwww.facebook.com\u002FLiker.Land\",\"https:\u002F\u002Fdiscord.gg\u002Flikecoin\"]}]","type":"application\u002Fld+json"}],"link":[{"rel":"preload","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Material+Icons","as":"style"},{"rel":"icon","type":"image\u002Fpng","href":"\u002Ffavicon.png"},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Material+Icons"}],"__dangerouslyDisableSanitizersByTagID":{"schema":["innerHTML"]},"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      errPageReady: false,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        nuxt.errPageReady = false
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_4de1dc0a === 'function') {
    await nuxt_plugin_sentryserver_4de1dc0a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_2faf2afc === 'function') {
    await nuxt_plugin_sentryclient_2faf2afc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_polyfill_14930cc5 === 'function') {
    await nuxt_plugin_polyfill_14930cc5(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_cosmosWalletclient_1b23f452 === 'function') {
    await nuxt_plugin_cosmosWalletclient_1b23f452(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuecookie_41e86efe === 'function') {
    await nuxt_plugin_vuecookie_41e86efe(app.context, inject)
  }

  if (typeof nuxt_plugin_vuei18n_9dc1c90a === 'function') {
    await nuxt_plugin_vuei18n_9dc1c90a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gtagclient_a0de0632 === 'function') {
    await nuxt_plugin_gtagclient_a0de0632(app.context, inject)
  }

  if (typeof nuxt_plugin_vuematerial_7da81004 === 'function') {
    await nuxt_plugin_vuematerial_7da81004(app.context, inject)
  }

  if (typeof nuxt_plugin_likecoinuivue_c930e148 === 'function') {
    await nuxt_plugin_likecoinuivue_c930e148(app.context, inject)
  }

  if (typeof nuxt_plugin_vuesimplesvg_73efa366 === 'function') {
    await nuxt_plugin_vuesimplesvg_73efa366(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueimagelightbox_7b24aa44 === 'function') {
    await nuxt_plugin_vueimagelightbox_7b24aa44(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueclipboard2_3c353cf9 === 'function') {
    await nuxt_plugin_vueclipboard2_3c353cf9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueportal_158772ee === 'function') {
    await nuxt_plugin_vueportal_158772ee(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuescrollreveal_36883210 === 'function') {
    await nuxt_plugin_vuescrollreveal_36883210(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuescroll_0c9cb62c === 'function') {
    await nuxt_plugin_vuescroll_0c9cb62c(app.context, inject)
  }

  if (typeof nuxt_plugin_vuejsmodal_f50827f4 === 'function') {
    await nuxt_plugin_vuejsmodal_f50827f4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gsapclient_05cf81a2 === 'function') {
    await nuxt_plugin_gsapclient_05cf81a2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_AuthCoreclient_2cbd409b === 'function') {
    await nuxt_plugin_AuthCoreclient_2cbd409b(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_crispclient_7cd6549f === 'function') {
    await nuxt_plugin_crispclient_7cd6549f(app.context, inject)
  }

  if (typeof nuxt_plugin_testing_dbf3344c === 'function') {
    await nuxt_plugin_testing_dbf3344c(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
